import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import {
  LayoutDashboard,
  CalendarDays,
  Hourglass,
  Heart,
  Users,
  ClipboardCheck,
  LayoutGrid,
  CalendarHeart,
  HandHeart,
  CheckSquare,
  Euro,
  Gift,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  Sparkles,
  FileText,
  Settings,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Lock,
  Bookmark,
  ChevronDown,
  ArrowUpRight,
} from "lucide-react";

import logoMain from "../assets/logo_main.png";
import CeremindAssistant from "./CeremindAssistant";

const categories = [
  {
    id: "couple",
    title: "Espace Couple",
    catIcon: Heart,
    defaultOpen: true,
    links: [
      { to: "/app",             label: "Tableau de bord",   icon: LayoutDashboard, end: true },
      { to: "/app/calendar",    label: "Calendrier",        icon: CalendarDays },
      { to: "/app/timeline",    label: "Planning",          icon: Hourglass },
      { to: "/app/moments",     label: "Moments",           icon: Heart },
    ]
  },
  {
    id: "guests",
    title: "Nos Invités",
    catIcon: Users,
    defaultOpen: true,
    links: [
      { to: "/app/guests",      label: "Invités",           icon: Users },
      { to: "/app/rsvp",        label: "Suivi des RSVP",    icon: ClipboardCheck },
      { to: "/app/guest-messages", label: "Messages invités", icon: MessageSquare, badge: "unread" },
    ]
  },
  {
    id: "planning",
    title: "Organisation & Logistique",
    catIcon: CalendarHeart,
    links: [
      { to: "/app/tables",      label: "Plan de table",     icon: LayoutGrid },
      { to: "/app/ceremony",    label: "Cérémonie",         icon: CalendarHeart },
      { to: "/app/team",        label: "Équipe",            icon: Users },
      { to: "/app/vendors",     label: "Prestataires",      icon: HandHeart },
      { to: "/app/tasks",       label: "Tâches",            icon: CheckSquare },
    ]
  },
  {
    id: "finance",
    title: "Finances & Cadeaux",
    catIcon: Gift,
    links: [
      { to: "/app/budget",      label: "Budget",            icon: Euro },
      { to: "/app/gifts",       label: "Cadeaux",           icon: Gift },
    ]
  },
  {
    id: "tools",
    title: "Communication & Outils",
    catIcon: Bookmark,
    links: [
      { to: "/app/site",        label: "Site de mariage",   icon: Globe },
      { to: "/app/invitations", label: "Invitations",       icon: Mail },
      { to: "/app/chat",        label: "Discussions",       icon: MessageCircle },
      { to: "/app/ai",          label: "Assistant IA",      icon: Sparkles, pro: true },
      { to: "/app/documents",   label: "Documents",         icon: FileText },
      { to: "/app/settings",    label: "Paramètres",        icon: Settings },
    ]
  }
];

function getActiveCategoryId(pathname, sections) {
  const matched = sections.find((section) =>
    section.links.some((link) => {
      if (pathname === link.to) {
        return true;
      }
      if (link.end) {
        return false;
      }
      return pathname.startsWith(`${link.to}/`);
    }),
  );
  return matched?.id || sections[0]?.id || null;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Capture de l'événement d'installation PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setDeferredPrompt(null);
    }
  };

  useEffect(() => {
    if (user) {
      api.get("/dashboard/stats").then(r => {
        setUnreadCount(r.data.guests_unread_messages || 0);
      }).catch(() => {});
    }
  }, [user]);

  const displayCategories = useMemo(() => {
    const items = [...categories];
    if (user?.role === "admin") {
      items.push({
        id: "admin",
        title: "Administration",
        defaultOpen: false,
        links: [{ to: "/app/admin", label: "Console Système", icon: ShieldCheck }]
      });
    }
    return items;
  }, [user?.role]);

  const activeCategoryId = getActiveCategoryId(location.pathname, displayCategories);
  const activeCategory = displayCategories.find((category) => category.id === activeCategoryId) || null;
  const activeLink = activeCategory?.links.find((link) => {
    if (location.pathname === link.to) {
      return true;
    }
    if (link.end) {
      return false;
    }
    return location.pathname.startsWith(`${link.to}/`);
  });

  useEffect(() => {
    setOpenSections((prev) => {
      const next = {};
      let changed = false;

      displayCategories.forEach((category) => {
        const shouldBeOpen =
          prev[category.id] ??
          category.defaultOpen ??
          false;

        next[category.id] = shouldBeOpen;
      });

      if (activeCategoryId && !next[activeCategoryId]) {
        next[activeCategoryId] = true;
      }

      if (Object.keys(prev).length !== Object.keys(next).length) {
        changed = true;
      } else {
        changed = Object.keys(next).some((key) => prev[key] !== next[key]);
      }

      return changed ? next : prev;
    });
  }, [activeCategoryId, displayCategories]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  return (
    <div className="app-shell min-h-screen text-charcoal flex flex-col lg:flex-row bg-transparent relative z-10">
      {/* Sidebar (Desktop) */}
      <aside
        className="app-shell-sidebar hidden h-screen w-[19rem] shrink-0 lg:sticky lg:top-0 lg:z-20 lg:flex lg:flex-col"
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="app-shell-brand px-5 pb-6 pt-8">
          <Link to="/" className="w-full flex justify-center flex-col items-center">
            <div className="w-48 flex justify-center">
              <img 
                src={logoMain} 
                alt="Ceremind Logo" 
                className="w-full h-auto object-contain reveal scale-110" 
              />
            </div>
            
            {/* The text is recreated perfectly sharp using HTML/CSS */}
            <div className="flex flex-col items-center gap-1 mt-3 text-center reveal" style={{ animationDelay: '150ms' }}>
              <span className="text-[6.5px] sm:text-[7px] font-bold tracking-[0.25em] text-charcoal-soft/80 uppercase">
                L'intelligence artificielle
              </span>
              <span className="text-[6.5px] sm:text-[7px] font-bold tracking-[0.25em] text-charcoal-soft/80 uppercase">
                Au service de votre grand jour
              </span>
            </div>
          </Link>
          <div className="app-shell-focus-card mt-6">
            <div className="app-shell-focus-row">
              <span className="app-shell-badge">Navigation</span>
              {unreadCount > 0 ? (
                <span className="app-shell-focus-chip">
                  {unreadCount} message{unreadCount > 1 ? "s" : ""}
                </span>
              ) : null}
            </div>
            <div className="mt-4">
              <p className="app-shell-focus-title">{activeCategory?.title || "Espace Couple"}</p>
              <p className="app-shell-focus-copy">
                {activeLink?.label
                  ? `Vous êtes dans ${activeLink.label.toLowerCase()}.`
                  : "Retrouvez tous vos espaces wedding dans une navigation claire et stable."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(activeLink?.to || "/app")}
              className="app-shell-focus-action mt-4"
            >
              Revenir à l'espace actif
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="app-shell-nav flex-1 overflow-y-auto px-3 py-5 space-y-5" data-lenis-prevent="true">
          {displayCategories.map((cat, idx) => (
            <div key={cat.title} className={`space-y-1 ${idx > 0 ? 'mt-6' : ''}`}>
              <div className="px-1 mb-3">
                <button
                  type="button"
                  onClick={() => toggleSection(cat.id)}
                  aria-expanded={openSections[cat.id] ? "true" : "false"}
                  aria-controls={`desktop-nav-section-${cat.id}`}
                  aria-label={`Afficher ou masquer la section ${cat.title}`}
                  className="app-shell-section-toggle"
                >
                  <span className="app-shell-section-label">
                    {cat.title}
                  </span>
                  <span className="app-shell-section-meta">
                    {cat.links.length}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${openSections[cat.id] ? "rotate-180" : ""}`}
                    />
                  </span>
                </button>
              </div>
              {openSections[cat.id] && (
                <div id={`desktop-nav-section-${cat.id}`} className="space-y-0.5">
                {cat.links.map((l) => {
                  const Icon = l.icon;
                  return (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.end}
                      data-testid={`nav-${l.to.replace(/\//g, "-")}`}
                      className={({ isActive }) =>
                        `app-shell-link group ${
                          isActive
                            ? "app-shell-link-active"
                            : "app-shell-link-idle"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 relative ${
                              isActive
                                ? "bg-sage/15 text-sage shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]"
                                : "text-charcoal-soft/60 group-hover:text-charcoal-soft"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {l.badge === "unread" && unreadCount > 0 && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-[#FAF9F6]"></span>
                            )}
                          </span>
                          <span className="flex-1 truncate">{l.label}</span>
                          {l.badge === "unread" && unreadCount > 0 && (
                            <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-sm">
                              {unreadCount}
                            </span>
                          )}
                          {l.pro && (
                            <span className="shrink-0 rounded-full bg-sage/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-sage">
                              IA
                            </span>
                          )}
                          {l.to === "/app/admin" && (
                            <ShieldCheck className="w-3 h-3 text-charcoal-soft/40 shrink-0" />
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
                </div>
              )}
            </div>
          ))}
        </nav>


        {/* User footer */}
        <div className="app-shell-footer p-5">
          <div className="app-shell-user-card mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sage/30 to-sage/10 text-[11px] font-bold text-sage">
              {user?.email?.charAt(0)?.toUpperCase() || ""}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate text-xs font-medium text-charcoal">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-charcoal-soft">
                {user?.role === "admin" ? "Administrateur" : "Compte Premium"}
              </div>
            </div>
          </div>
          {isInstallable && (
            <button
              onClick={installApp}
              className="app-shell-utility-button mb-2 w-full justify-center text-sage hover:text-sage/80"
            >
              Installer l'application
            </button>
          )}
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-button"
            className="app-shell-utility-button w-full justify-center text-charcoal-soft hover:border-red-200 hover:bg-red-50/50 hover:text-red-400"
          >
            <LogOut className="w-3 h-3" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Global Mobile Header */}
      <header className="app-shell-mobile-header lg:hidden sticky top-0 z-40 flex h-[calc(5rem+env(safe-area-inset-top))] shrink-0 items-center justify-between px-4 pt-[calc(env(safe-area-inset-top)+0.85rem)]">
        <button
          onClick={() => setIsMenuOpen(true)}
          aria-label="Ouvrir le menu principal"
          className="app-shell-mobile-icon -ml-2"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
        
        <button
          type="button"
          onClick={() => navigate('/app')}
          aria-label="Retourner à l'accueil Ceremind"
          className="app-shell-mobile-brand"
        >
          <span className="app-shell-badge">Workspace</span>
          <h1 className="font-serif text-lg font-medium tracking-wide text-charcoal">Ceremind</h1>
          <div className="mt-1 flex items-center gap-1 text-[10px] text-[#8d8b84]">
            <Lock className="w-2.5 h-2.5" />
            <span>Chiffré</span>
          </div>
        </button>
        
        <button
          onClick={() => navigate('/app')}
          aria-label="Retourner au tableau de bord"
          className="app-shell-mobile-icon -mr-2"
        >
          <LayoutGrid className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </header>

      {/* Global Mobile Slide-Over Menu */}
      <div className={`lg:hidden fixed inset-0 z-50 transition-all duration-300 ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        <div 
          onClick={() => setIsMenuOpen(false)}
          className={`absolute inset-0 bg-[#FCFBF9]/40 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`} 
        />
        
        <div className={`app-shell-mobile-panel absolute bottom-0 left-0 top-0 flex w-[82%] max-w-sm flex-col shadow-2xl transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-start justify-between border-b border-white/40 px-6 py-6">
            <div>
              <span className="app-shell-badge">Navigation</span>
              <h2 className="mt-2 font-serif text-2xl text-charcoal">Ceremind</h2>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              aria-label="Fermer le menu principal"
              className="app-shell-mobile-icon -mr-2"
            >
              <X className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
          
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-6" data-lenis-prevent="true">
            {displayCategories.map((cat) => (
              <div key={cat.title} className="app-shell-mobile-section">
                <button
                  type="button"
                  onClick={() => toggleSection(cat.id)}
                  aria-expanded={openSections[cat.id] ? "true" : "false"}
                  aria-controls={`mobile-nav-section-${cat.id}`}
                  aria-label={`Afficher ou masquer la section ${cat.title}`}
                  className="app-shell-mobile-section-toggle"
                >
                  <span className="app-shell-section-label">
                    {cat.title}
                  </span>
                  <span className="app-shell-section-meta">
                    {cat.links.length}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${openSections[cat.id] ? 'rotate-180' : ''}`}
                    />
                  </span>
                </button>
                {openSections[cat.id] && (
                <div id={`mobile-nav-section-${cat.id}`} className="mt-3 flex flex-col gap-3">
                  {cat.links.map((l) => {
                    const Icon = l.icon;
                    return (
                      <button
                        key={l.to}
                        onClick={() => { setIsMenuOpen(false); navigate(l.to); }}
                        className="app-shell-mobile-link"
                      >
                        <span className="app-shell-mobile-link-icon">
                          <Icon className="w-4 h-4 text-charcoal-soft/60" />
                        </span>
                        <span className="flex-1 font-serif text-xl">{l.label}</span>
                        {l.pro && (
                          <span className="rounded-full bg-sage/10 px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-sage">
                            IA
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
            
            {/* Install Button if installable */}
            {isInstallable && (
              <button
                onClick={() => { setIsMenuOpen(false); installApp(); }}
                className="mt-4 w-full rounded-2xl bg-[#8A9A86] py-3 text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-[#8A9A86]/90"
              >
                Installer l'application
              </button>
            )}
            
            {!isStandalone && isIOS && (
              <div className="mt-4 rounded-2xl border border-white/40 bg-white/65 p-4 text-[11px] font-light leading-relaxed text-charcoal-soft shadow-[0_18px_42px_-34px_rgba(83,64,52,0.38)]">
                💡 Pour installer sur iPhone/iPad : cliquez sur le bouton <span className="font-semibold text-charcoal">Partager</span> dans Safari, puis sélectionnez <span className="font-semibold text-charcoal">"Sur l'écran d'accueil"</span>.
              </div>
            )}
          </div>

          <div className="app-shell-mobile-footer flex flex-col gap-4 border-t border-white/40 px-8 py-8">
            <button onClick={() => { setIsMenuOpen(false); navigate('/app/settings'); }} className="flex items-center gap-3 text-sm text-[#4A4A4A] hover:text-[#2C2C2C]">
              <Settings className="w-4 h-4" /> Paramètres
            </button>
            <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-3 text-sm text-[#4A4A4A] hover:text-[#2C2C2C]">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-x-hidden relative flex flex-col">
        <Outlet />
      </main>

      <CeremindAssistant />
    </div>
  );
}
